import React, { useEffect, useMemo, useState } from "react";
import {
  MdShield,
  MdAdd,
  MdDelete,
  MdLock,
  MdPeople,
  MdSearch,
} from "react-icons/md";
import { toast } from "react-toastify";
import Card from "../../../components/card";
import { PermissionInfo, Profile, UserProfiles } from "../../../interfaces/Profile";
import {
  assignProfiles,
  createProfile,
  deleteProfile,
  getPermissionCatalog,
  getProfiles,
  getUserProfiles,
  updateProfile,
} from "../../../services/profileApi";
import { usePermissions } from "../../../auth/PermissionProvider";
import { Permissions } from "../../../auth/permissions";
import { mensagemDoServidor } from "../../../utils/mensagemDoServidor";
import AlcanceBadge from "./components/AlcanceBadge";
import ProfileEditor from "./components/ProfileEditor";
import UserProfilesTable from "./components/UserProfilesTable";

type Tab = "profiles" | "users";

/**
 * Gestão de perfis de acesso.
 *
 * A tela responde duas perguntas que o portal não sabia responder: **o que este perfil pode** e
 * **quem tem este perfil**. Cada checkbox aqui é a mesma chave que o `[Authorize(Policy=…)]` do
 * backend exige — não há tradução no meio, e é por isso que marcar uma caixa muda acesso de
 * verdade, ao contrário da antiga tela de papéis.
 */
const ProfilesPage: React.FC = () => {
  const { can, refresh } = usePermissions();

  const [tab, setTab] = useState<Tab>("profiles");
  const [catalog, setCatalog] = useState<PermissionInfo[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [users, setUsers] = useState<UserProfiles[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const selected = profiles.find((p) => p.id === selectedId) ?? null;

  const load = async () => {
    setLoading(true);
    try {
      const [cat, list, usrs] = await Promise.all([
        getPermissionCatalog(),
        getProfiles(),
        getUserProfiles(),
      ]);
      setCatalog(cat);
      setProfiles(list);
      setUsers(usrs);
      setSelectedId((current) => current ?? list[0]?.id ?? null);
    } catch (e) {
      console.error("Erro ao carregar perfis", e);
      toast.error("Falha ao carregar os perfis de acesso.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      // Nasce sem nenhuma permissão de propósito: um perfil novo não deve conceder nada até
      // alguém dizer, item por item, o que ele concede.
      const created = await createProfile({ name, description: null, permissions: [] });
      setProfiles((prev) => [...prev, created]);
      setSelectedId(created.id);
      setNewName("");
      setCreating(false);
      toast.success(`Perfil "${created.name}" criado. Marque o que ele pode acessar.`);
    } catch (e: any) {
      toast.error(mensagemDoServidor(e, "Não foi possível criar o perfil."));
    }
  };

  const handleDelete = async (profile: Profile) => {
    if (!window.confirm(`Excluir o perfil "${profile.name}"? Esta ação não pode ser desfeita.`)) {
      return;
    }
    try {
      await deleteProfile(profile.id);
      setProfiles((prev) => prev.filter((p) => p.id !== profile.id));
      setSelectedId((current) => (current === profile.id ? null : current));
      toast.success("Perfil excluído.");
    } catch (e: any) {
      toast.error(mensagemDoServidor(e, "Não foi possível excluir o perfil."));
    }
  };

  const handleSave = async (
    profile: Profile,
    changes: { name: string; description: string | null; permissions: string[] }
  ) => {
    try {
      const saved = await updateProfile(profile.id, changes);
      setProfiles((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
      toast.success(`Perfil "${saved.name}" salvo. Já vale para quem o tem.`);
      // O próprio usuário pode ter acabado de mudar o que ele mesmo enxerga.
      await refresh();
    } catch (e: any) {
      toast.error(mensagemDoServidor(e, "Não foi possível salvar o perfil."));
    }
  };

  const handleAssign = async (userId: string, profileIds: number[]) => {
    try {
      await assignProfiles(userId, profileIds);
      setUsers((prev) => prev.map((u) => (u.userId === userId ? { ...u, profileIds } : u)));
      // Os contadores "N pessoas" da lista da esquerda dependem disto.
      setProfiles(await getProfiles());
      toast.success("Perfis do usuário atualizados.");
      await refresh();
    } catch (e: any) {
      // O 400 de validação vem como texto puro; o 403 da D6, como { message }.
      toast.error(mensagemDoServidor(e, "Não foi possível atribuir os perfis."));
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter(
      (p) =>
        p.name.toLowerCase().includes(q) || (p.description ?? "").toLowerCase().includes(q)
    );
  }, [profiles, search]);

  if (!can(Permissions.ProfilesManage)) {
    return null;
  }

  return (
    <div className="mt-3 flex flex-col gap-3">
      <Card extra="w-full px-6 py-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-500/10 text-brand-500">
              <MdShield className="h-6 w-6" />
            </span>
            <div>
              <h1 className="text-xl font-bold text-navy-700 dark:text-white">
                Perfis de acesso
              </h1>
              <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
                Cada permissão marcada aqui é exatamente a que o servidor exige. Alterações valem
                no próximo clique de quem tem o perfil — sem precisar sair e entrar de novo.
              </p>
              <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
                Perfis externos se atribuem no vínculo da pessoa (Acessos, Pessoas ou Painéis);
                internos, na aba Usuários.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-xl bg-gray-50 p-1 dark:bg-navy-900">
            {(
              [
                ["profiles", "Perfis", MdShield],
                ["users", "Usuários", MdPeople],
              ] as [Tab, string, typeof MdShield][]
            ).map(([key, label, Icon]) => (
              <button
                key={key}
                type="button"
                onClick={() => setTab(key)}
                className={`flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition-all ${
                  tab === key
                    ? "bg-white text-navy-700 shadow-sm dark:bg-navy-700 dark:text-white"
                    : "text-gray-600 hover:text-navy-700 dark:text-gray-400 dark:hover:text-white"
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </Card>

      {loading ? (
        <Card extra="w-full px-6 py-16">
          <p className="text-center text-sm text-gray-500 dark:text-gray-400">Carregando…</p>
        </Card>
      ) : tab === "users" ? (
        <UserProfilesTable users={users} profiles={profiles} onAssign={handleAssign} />
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[300px_1fr]">
          {/* Lista de perfis */}
          <Card extra="w-full px-4 py-4 self-start">
            <div className="mb-3 flex items-center gap-2">
              <div className="relative flex-1">
                <MdSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar perfil"
                  className="w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-800 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                />
              </div>
              <button
                type="button"
                onClick={() => setCreating((v) => !v)}
                title="Novo perfil"
                aria-expanded={creating}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-dv-gradient text-white shadow-dv-glow transition-transform hover:scale-105"
              >
                <MdAdd className={`h-5 w-5 transition-transform ${creating ? "rotate-45" : ""}`} />
              </button>
            </div>

            {creating && (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleCreate();
                }}
                className="mb-3 flex gap-2"
              >
                <input
                  autoFocus
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Nome do perfil"
                  className="min-w-0 flex-1 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 outline-none focus:border-brand-500 dark:border-white/10 dark:bg-navy-900 dark:text-white"
                />
                <button
                  type="submit"
                  disabled={!newName.trim()}
                  className="shrink-0 rounded-xl bg-brand-500 px-3 py-2 text-sm font-semibold text-white disabled:opacity-40"
                >
                  Criar
                </button>
              </form>
            )}

            <ul className="flex flex-col gap-1">
              {filtered.map((p) => {
                const active = p.id === selectedId;
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(p.id)}
                      className={`group flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2.5 text-left transition-all ${
                        active
                          ? "bg-brand-500/10 text-navy-700 dark:text-white"
                          : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                      }`}
                    >
                      <span className="min-w-0">
                        <span className="flex items-center gap-1.5">
                          <span className="truncate text-sm font-semibold">{p.name}</span>
                          {p.isSystem && (
                            <MdLock
                              className="h-3.5 w-3.5 shrink-0 text-gray-400"
                              title="Perfil de sistema: o nome é fixo e ele não pode ser excluído"
                            />
                          )}
                          <AlcanceBadge alcance={p.alcance} />
                        </span>
                        <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                          {p.permissions.length} permissões · {p.userCount}{" "}
                          {p.userCount === 1 ? "pessoa" : "pessoas"}
                        </span>
                      </span>
                      {!p.isSystem && (
                        <span
                          role="button"
                          tabIndex={0}
                          aria-label={`Excluir ${p.name}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(p);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.stopPropagation();
                              handleDelete(p);
                            }
                          }}
                          className="shrink-0 rounded-lg p-1 text-gray-400 opacity-0 transition-opacity hover:bg-red-500/10 hover:text-red-500 group-hover:opacity-100"
                        >
                          <MdDelete className="h-4 w-4" />
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
              {filtered.length === 0 && (
                <li className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                  Nenhum perfil encontrado.
                </li>
              )}
            </ul>
          </Card>

          {/* Matriz de permissões */}
          {selected ? (
            <ProfileEditor
              key={selected.id}
              profile={selected}
              catalog={catalog}
              onSave={(changes) => handleSave(selected, changes)}
            />
          ) : (
            <Card extra="w-full px-6 py-16">
              <p className="text-center text-sm text-gray-500 dark:text-gray-400">
                Selecione um perfil à esquerda para ver o que ele libera.
              </p>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfilesPage;
